import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';

import { getUserProfile, User } from '@topconagriculture/profiling';

import { SharingPartner } from '../../core/api/sharing/sharing.types';
import { Feeding } from '../entities/feeding/feeding.types';
import { getGroupList, getGroupsNoPremixes } from '../entities/group/group.selector';
import { Group } from '../entities/group/group.types';
import { getIngredients } from '../entities/ingredients/ingredients.selector';
import { Ingredient } from '../entities/ingredients/ingredients.types';
import { getMixersList } from '../entities/mixers/mixers.selector';
import { getPremixes } from '../entities/premixes/premixes.selector';
import { Premix } from '../entities/premixes/premixes.types';
import { getRecipeById, getRecipes } from '../entities/recipes/recipes.selector';
import { Recipe } from '../entities/recipes/recipes.types';
import { reportDownloadFormats } from '../entities/reports/reports.constants';
import { ReportFormat } from '../entities/reports/reports.types';
import { Forms } from '../form/form.selector';
import { RootState } from '../reducers';
import { LoadsDeliveriesRationContentForm } from '../../routes/Manage/DataTools/DataToolsPage/DataToolsSpecificPage/DataToolsSpecificPageContent/LoadDeliveriesDataTools/LoadDeliveriesContent/LoadsDeliveriesRationContent/LoadsDeliveriesRationContent';
import { SettingsType } from '../entities/settings/settings.types';
import { getCompanies } from '../entities/company/company.selector';
import { Company } from '../entities/company/company.types';
import { getLots } from '../entities/lots/lots.selector';
import { Lot } from '../entities/lots/lots.types';

export const getSelectedIngredientId = (state: RootState): string | null => state.selected.ingredientId;
export const getSelectedPremixId = (state: RootState): string | null => state.selected.premixId;
export const getSelectedRecipeId = (state: RootState): string | null => state.selected.recipeId;

export const getSelectedFeedingId = (state: RootState): string | null => state.selected.feedingId || null;
export const getSelectedFeedingPeriodId = (state: RootState): string | null => state.selected.feedingPeriodId || null;

export const getSelectedGroupId = (state: RootState): string | null => state.selected.groupId || null;

export const getSelectedMixerId = (state: RootState): string | null => state.selected.mixerId || null;
export const getSelectedReportId = (state: RootState): string | null => state.selected.reportId || null;

export const getSelectedPartner = (state: RootState): SharingPartner | null => state.selected.selectedPartner;

export const getCurrentFarmId = (state: RootState): string | null => state.selected.currentFarmId;

export const getSelectedCompanyId = (state: RootState): string | null => state.selected.companyId;

export const getSelectedReportFormat = (state: RootState): ReportFormat =>
  reportDownloadFormats.find((reportFormat) => reportFormat.id === state.selected.reportFormatId) as ReportFormat;

export const getReportFormat = (state: RootState, id: string): ReportFormat =>
  reportDownloadFormats.find((reportFormat) => reportFormat.id === id) as ReportFormat;

export const isOwnCompanySelected = createSelector(
  [getCompanies, getSelectedCompanyId, getUserProfile],
  (companies: Company[], id: string | null, user: User | null) =>
    user?.company.id === companies.find((elem) => elem.id === id)?.id,
);

export const getSelectedGroup = createSelector(
  [getGroupList, getSelectedGroupId],
  (groupList, selectedGroupId): Group => {
    return groupList.find((group) => group.id === selectedGroupId)!;
  },
);

export const getSelectedMixer = createSelector([getMixersList, getSelectedMixerId], (mixerList, selectedMixerId) => {
  return mixerList.find((mixer) => mixer.id === selectedMixerId);
});

export const getSelectedGroupFeeding = createSelector(
  [getGroupList, getSelectedGroupId, getSelectedFeedingId],
  (groupList, selectedGroupId, selectedFeedingId) => {
    const selectedGroup = groupList.find((group) => group.id === selectedGroupId);
    return selectedGroup
      ? (selectedGroup.feedings || []).find((feeding: Feeding) => feeding.id === selectedFeedingId)
      : {};
  },
);

export const isGroupSelected = (state: RootState): boolean => !!state.selected.groupId;
export const isMixerSelected = (state: RootState): boolean => !!state.selected.mixerId;

export const getSelectedIngredient = createSelector(
  [getIngredients, getSelectedIngredientId],
  (ingredientList: Ingredient[], selectedIngredientId: string): Ingredient | undefined => {
    return ingredientList && ingredientList.find((ingredient: Ingredient) => ingredient.id === selectedIngredientId);
  },
);

export const getSelectedPremix = createSelector(
  [getPremixes, getSelectedPremixId],
  (premixList: Premix[], selectedPremixId: string): Premix | undefined => {
    return premixList && premixList.find((premix: Premix) => premix.id === selectedPremixId);
  },
);

export const getSelectedRecipe = createSelector(
  [getRecipes, getSelectedRecipeId],
  (recipesList: Recipe[], selectedRecipeId: string): Recipe | undefined => {
    return recipesList?.find((recipe: Recipe) => recipe.id === selectedRecipeId);
  },
);

export const hasSelectedRecipeMixerCommands = createSelector(
  [getSelectedRecipe, getRecipeById],
  (recipe, getRecipe) => {
    const baseRation = getRecipe(recipe?.baseRationId || '');
    return (
      recipe?.ingredients?.some(
        (ingredient) => typeof ingredient.mixingValue === 'number' && ingredient.mixingValue !== 0,
      ) ||
      (!baseRation
        ? true
        : baseRation.ingredients?.some(
            (ingredient) => typeof ingredient.mixingValue === 'number' && ingredient.mixingValue !== 0,
          ))
    );
  },
);

export const hasSelectedPremixMixerCommands = createSelector([getSelectedPremix], (premix) => {
  return premix?.ingredients?.some(
    (ingredient) => typeof ingredient.mixingValue === 'number' && ingredient.mixingValue !== 0,
  );
});

const feedingCreationForm = formValueSelector(Forms.feedingCreationForm);
const selectedRecipeForm = (state: RootState) => feedingCreationForm(state, 'recipe');

const feedingEditRecipeForm = formValueSelector(Forms.feedingEditRecipeForm);
const selectedEditRecipeForm = (state: RootState) => feedingEditRecipeForm(state, 'recipe');

export const getSelectedRecipeInFeedingCreation = createSelector(
  [getRecipes, selectedRecipeForm],
  (recipeList, selectedRecipeId) => recipeList.find((r) => r.id === selectedRecipeId),
);
export const getSelectedRecipeInFeedingEditing = createSelector(
  [getRecipes, getPremixes, selectedEditRecipeForm],
  (recipeList, premixList, selectedRecipeId) => [...recipeList, ...premixList].find((r) => r.id === selectedRecipeId),
);

export const getFocusedGroupId = (state: RootState): number => state.selected.focusedGroupId;
export const getFocusedGroupField = (state: RootState): number => state.selected.focusedGroupField;

export const getFocusedRecipeId = (state: RootState): number => state.selected.focusedRecipeId;

export const getFocusedPremixId = (state: RootState): number => state.selected.focusedPremixId;

export const getFocusedMatterId = (state: RootState): number => state.selected.focusedMatterId;
export const getFocusedMatterField = (state: RootState): number => state.selected.focusedMatterField;

export const getSelectedLoadDeliveryRation = (state: RootState): LoadsDeliveriesRationContentForm | undefined =>
  state.selected.selectedLoadDeliveryRation;

export const getSelectedLotId = (state: RootState): string | undefined => state.selected.selectedLotId;

export const getSelectedLoadMixersPremix = (state: RootState): string | undefined =>
  state.selected.selectedLoadMixersPremix;

export const getSelectedSettingsPanel = (state: RootState): SettingsType => state.selected.seletedSettingsPanel;
export const getSelectedOrderId = (state: RootState): string | undefined => state.selected.selectedOrder;

export const getSelectedWeighbridgeTicketId = (state: RootState): string | null =>
  state.selected.selectedWeighbridgeTicket;

export const getSelectedLot = createSelector([getLots, getSelectedLotId], (lots: Lot[], selectedLotId: string) => {
  return lots.find((lot) => lot.id === selectedLotId);
});

export const getSelectedBunkReaderPenId = (state: RootState): string | null => state.selected.selectedBunkReaderPen;
export const getSelectedBunkReaderPen = createSelector(
  [getSelectedBunkReaderPenId, getGroupsNoPremixes],
  (selectedBunkReaderPenId: string | null, groups: Group[]) => {
    return groups.find((group) => group.id === selectedBunkReaderPenId);
  },
);
